import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { saveBranchSmsConfig } from '../../services/branchService'
import { apiError } from '../../api/apiError'
import type { BranchSmsConfig } from '../../types'
import '../BranchForm/BranchFormModal.css'

interface Props {
  branchId: string
  config: BranchSmsConfig | null
  onClose: () => void
  onSaved: (c: BranchSmsConfig) => void
}

// форматы совпадают с валидацией на бэке (AdminBranchSmsConfigRequest)
const ACCOUNT_SID_RE = /^AC[0-9a-fA-F]{32}$/
const API_KEY_SID_RE = /^SK[0-9a-fA-F]{32}$/

export default function BranchSmsConfigModal({ branchId, config, onClose, onSaved }: Props) {
  const isEdit = !!config
  const { t } = useTranslation()

  const [accountSid, setAccountSid] = useState(config?.accountSid ?? '')
  const [apiKeySid, setApiKeySid] = useState(config?.apiKeySid ?? '')
  const [apiKeySecret, setApiKeySecret] = useState('')
  const [fromNumber, setFromNumber] = useState(config?.fromNumber ?? '')
  const [enabled, setEnabled] = useState(config?.enabled ?? true)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  function validate() {
    const e: Record<string, string> = {}
    if (!ACCOUNT_SID_RE.test(accountSid.trim())) e.accountSid = t('admin.branch_sms.invalid_account_sid')
    if (!API_KEY_SID_RE.test(apiKeySid.trim())) e.apiKeySid = t('admin.branch_sms.invalid_api_key_sid')
    if (!isEdit && !apiKeySecret.trim()) e.apiKeySecret = t('admin.branch_sms.required')
    if (!fromNumber.trim()) e.fromNumber = t('admin.branch_sms.required')
    setErrors(e)
    return Object.keys(e).length === 0
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validate()) return
    setLoading(true)
    try {
      const result = await saveBranchSmsConfig(branchId, {
        accountSid: accountSid.trim(),
        apiKeySid: apiKeySid.trim(),
        apiKeySecret: apiKeySecret.trim() || undefined,
        fromNumber: fromNumber.trim(),
        enabled,
      })
      onSaved(result)
    } catch (err) {
      const { code, message } = apiError(err)
      if (code === 'PHONE_INVALID') setErrors({ fromNumber: t('admin.branch_sms.invalid_phone') })
      else if (code === 'API_KEY_SECRET_REQUIRED') setErrors({ apiKeySecret: t('admin.branch_sms.required') })
      else setErrors({ _: message || t('admin.branch_sms.save_failed') })
    } finally {
      setLoading(false)
    }
  }

  function field(key: string, label: string, value: string, setValue: (v: string) => void, opts?: { type?: string; placeholder?: string; hint?: string }) {
    return (
      <div className="modal__field">
        <label className="modal__label">{label}</label>
        <input
          className={`admin-input modal__input${errors[key] ? ' admin-input--error' : ''}`}
          type={opts?.type ?? 'text'}
          value={value}
          placeholder={opts?.placeholder}
          autoComplete="off"
          onChange={(e) => { setValue(e.target.value); setErrors((p) => ({ ...p, [key]: '' })) }}
          disabled={loading}
        />
        {errors[key]
          ? <span className="modal__error">{errors[key]}</span>
          : opts?.hint && <span className="branch-sms__hint">{opts.hint}</span>}
      </div>
    )
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <h2 className="modal__title">{t('admin.branch_sms.modal_title')}</h2>
          <button className="modal__close" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M2 2l12 12M14 2L2 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <form className="modal__body" onSubmit={handleSubmit}>
          {field('accountSid', t('admin.branch_sms.label_account_sid'), accountSid, setAccountSid, { placeholder: 'AC…' })}
          {field('apiKeySid', t('admin.branch_sms.label_api_key_sid'), apiKeySid, setApiKeySid, { placeholder: 'SK…' })}
          {field('apiKeySecret', t('admin.branch_sms.label_api_key_secret'), apiKeySecret, setApiKeySecret, {
            type: 'password',
            placeholder: isEdit && config?.apiKeySecretSet ? t('admin.branch_sms.secret_keep_placeholder') : '',
            hint: t('admin.branch_sms.secret_hint'),
          })}
          {field('fromNumber', t('admin.branch_sms.label_from_number'), fromNumber, setFromNumber, {
            placeholder: '+18885551234',
            hint: t('admin.branch_sms.from_number_hint'),
          })}

          <label className="branch-sms__checkbox">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} disabled={loading} />
            {t('admin.branch_sms.label_enabled')}
          </label>

          {errors._ && <p className="modal__error">{errors._}</p>}

          <div className="modal__footer">
            <button type="button" className="admin-btn admin-btn--secondary" onClick={onClose} disabled={loading}>
              {t('admin.branch_sms.cancel')}
            </button>
            <button type="submit" className="admin-btn admin-btn--primary" disabled={loading}>
              {loading ? t('admin.branch_sms.saving') : t('admin.branch_sms.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
